import React, { useEffect, useRef, useState } from 'react';
import { ScanLine, UploadCloud, Loader2, CheckCircle2, AlertTriangle, XCircle, Link2, PlusCircle, Pencil, Trash2, Eye, Undo2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { saasUploadInvoice, saasListInvoices, saasPatchInvoice, saasPostInvoice, saasUnpostInvoice, saasRematchInvoice, saasDeleteInvoice, saasInvoiceFileUrl } from '../../api';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CATS = ['sales', 'expense', 'salary', 'vat', 'tax', 'other'];

const STATUS = {
  extracted: { en: 'Ready to post', ar: 'جاهزة للترحيل', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: CheckCircle2 },
  needs_review: { en: 'Needs review', ar: 'تحتاج مراجعة', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle },
  posted: { en: 'Posted', ar: 'مُرحّلة', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  failed: { en: 'Read failed', ar: 'فشلت القراءة', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
};

const Field = ({ k, label, form, setForm, type = 'text' }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#111827]/50 dark:text-slate-400 font-bold">{label}</label>
    <Input type={type} value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9 mt-1 bg-white dark:bg-slate-800" data-testid={`inv-form-${k}`} />
  </div>
);

const UploadQueue = ({ lang, queue }) => queue.length ? (
  <div className="mt-4 space-y-1.5" data-testid="invoice-upload-queue">
    {queue.map((q) => (
      <div key={q.key} className="flex items-center gap-2 text-xs" data-testid={`queue-item-${q.state}`}>
        {q.state === 'reading' && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2563eb]" />}
        {q.state === 'queued' && <span className="w-3.5 h-3.5 rounded-full border border-[#111827]/30 inline-block" />}
        {q.state === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
        {q.state === 'error' && <XCircle className="w-3.5 h-3.5 text-rose-600" />}
        <span className="truncate flex-1 text-[#111827] dark:text-slate-200">{q.name}</span>
        <span className="text-[#111827]/50 dark:text-slate-400">
          {q.state === 'reading' ? (lang === 'ar' ? 'جارٍ القراءة بالذكاء الاصطناعي…' : 'Reading with AI…') : q.state === 'done' ? (q.result?.vendor || q.result?.customer || '') : q.state === 'error' ? q.error : (lang === 'ar' ? 'في الانتظار' : 'Queued')}
        </span>
      </div>
    ))}
  </div>
) : null;

export const InvoicesPanel = ({ lang, company, onPosted }) => {
  const ar = lang === 'ar';
  const [data, setData] = useState({ invoices: [], summary: {} });
  const [filter, setFilter] = useState('');
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [acting, setActing] = useState('');
  const [selected, setSelected] = useState([]);
  const fileRef = useRef(null);
  const onPick = () => setSelected(Array.from(fileRef.current?.files || []).map((f) => ({ name: f.name, size: f.size })));
  const clearPick = (e) => { e.preventDefault(); if (fileRef.current) fileRef.current.value = ''; setSelected([]); };

  const load = async () => {
    try { setData(await saasListInvoices(company || undefined, filter || undefined)); } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [company, filter]);

  const upload = async () => {
    const files = Array.from(fileRef.current?.files || []);
    if (!files.length) { toast({ title: ar ? 'اختر ملفات الفواتير أولاً' : 'Choose invoice files first', variant: 'destructive' }); return; }
    setBusy(true);
    const q = files.map((f, i) => ({ key: `${Date.now()}-${i}`, name: f.name, state: 'queued', file: f }));
    setQueue(q);
    let ok = 0;
    for (let i = 0; i < q.length; i += 1) {
      setQueue((cur) => cur.map((x, j) => (j === i ? { ...x, state: 'reading' } : x)));
      try {
        const res = await saasUploadInvoice(q[i].file, company || '');
        ok += 1;
        setQueue((cur) => cur.map((x, j) => (j === i ? { ...x, state: 'done', result: res } : x)));
      } catch (e) {
        setQueue((cur) => cur.map((x, j) => (j === i ? { ...x, state: 'error', error: e?.response?.data?.detail || 'Failed' } : x)));
      }
      load();
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    setSelected([]);
    toast({ title: ar ? `تمت قراءة ${ok} من ${q.length} فاتورة` : `${ok} of ${q.length} invoices read` });
  };

  const act = async (id, fn, okMsg) => {
    setActing(id);
    try { await fn(); if (okMsg) toast({ title: okMsg }); await load(); onPosted?.(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setActing(''); }
  };

  const startEdit = (inv) => {
    setEditing(inv);
    setForm({ kind: inv.kind, vendor: inv.vendor || '', customer: inv.customer || '', invoice_number: inv.invoice_number || '', invoice_date: inv.invoice_date || '', subtotal: inv.subtotal ?? '', vat_amount: inv.vat_amount ?? '', total: inv.total ?? '', vendor_trn: inv.vendor_trn || '', customer_trn: inv.customer_trn || '', category: inv.category || 'expense' });
  };
  const saveEdit = async () => {
    const payload = { ...form };
    ['subtotal', 'vat_amount', 'total'].forEach((k) => { payload[k] = payload[k] === '' ? null : Number(payload[k]); });
    await act(editing.id, () => saasPatchInvoice(editing.id, payload), ar ? 'تم حفظ التعديلات' : 'Saved');
    setEditing(null);
  };

  const s = data.summary || {};
  return (
    <>
      <div className="glass-card rounded-xl p-6 mb-6" data-testid="invoices-upload-card">
        <div className="flex items-start gap-3 mb-4">
          <ScanLine className="w-7 h-7 text-[#2563eb] flex-shrink-0" />
          <div>
            <h2 className="font-display text-2xl text-[#0a0a0a] dark:text-white">{ar ? 'قراءة الفواتير بالذكاء الاصطناعي' : 'AI Invoice Reader'}</h2>
            <p className="text-sm text-[#111827]/60 dark:text-slate-400">{ar ? 'ارفع مجموعة فواتير (صور أو PDF) — يستخرج الذكاء الاصطناعي المورّد، الرقم، التاريخ، الضريبة، الإجمالي وTRN، ويقترح مطابقتها مع عمليات البنك.' : 'Upload a batch of invoices (images or PDF) — AI extracts vendor, number, date, VAT, total & TRN and suggests matches with bank transactions.'}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <label className={`flex-1 flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 cursor-pointer transition-colors ${selected.length ? 'border-[#2563eb] bg-blue-50/60 dark:bg-blue-950/30' : 'border-[#2563eb]/30 bg-[#f8fafc] dark:bg-slate-900 hover:border-[#2563eb]/60'}`} data-testid="invoice-dropzone">
            {selected.length ? <CheckCircle2 className="w-5 h-5 text-[#2563eb] flex-shrink-0" /> : <UploadCloud className="w-5 h-5 text-[#2563eb] flex-shrink-0" />}
            {selected.length ? (
              <div className="flex-1 min-w-0" data-testid="invoice-selected-files">
                <div className="text-sm font-semibold text-[#0a0a0a] dark:text-white">{ar ? `تم اختيار ${selected.length} ${selected.length === 1 ? 'ملف' : 'ملفات'} — اضغط "رفع وقراءة"` : `${selected.length} file${selected.length === 1 ? '' : 's'} selected — click "Upload & Read"`}</div>
                <div className="text-[11px] text-[#111827]/60 dark:text-slate-400 truncate">{selected.map((f) => f.name).join(' · ')}</div>
              </div>
            ) : (
              <span className="text-sm text-[#111827]/70 dark:text-slate-300">{ar ? 'اختر عدة فواتير (JPG / PNG / PDF، حتى 10MB لكل ملف)' : 'Select multiple invoices (JPG / PNG / PDF, up to 10MB each)'}</span>
            )}
            {selected.length > 0 && !busy && (
              <button onClick={clearPick} className="text-[11px] text-rose-600 hover:underline flex-shrink-0" data-testid="invoice-clear-files">{ar ? 'إزالة' : 'Clear'}</button>
            )}
            <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={onPick} data-testid="invoice-file-input" />
          </label>
          <Button onClick={upload} disabled={busy || !selected.length} className="gold-button rounded-md h-12 px-6 text-sm font-bold" data-testid="invoice-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (ar ? `رفع وقراءة${selected.length ? ` (${selected.length})` : ''}` : `Upload & Read${selected.length ? ` (${selected.length})` : ''}`)}
          </Button>
        </div>
        <UploadQueue lang={lang} queue={queue} />
      </div>

      <div className="glass-card rounded-xl p-6" data-testid="invoices-list-card">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h3 className="font-display text-lg text-[#0a0a0a] dark:text-white">{ar ? 'الفواتير' : 'Invoices'}</h3>
          <div className="flex gap-2 text-[11px]" data-testid="invoices-summary">
            <span className="px-2 py-0.5 rounded bg-[#f1f5f9] dark:bg-slate-800 text-[#111827]/70 dark:text-slate-300">{ar ? 'الكل' : 'All'} {s.total || 0}</span>
            <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">{ar ? 'بانتظار الترحيل' : 'Pending'} {s.pending || 0}</span>
            <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">{ar ? 'مُرحّلة' : 'Posted'} {s.posted || 0}</span>
            {s.failed > 0 && <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700">{ar ? 'فشلت' : 'Failed'} {s.failed}</span>}
          </div>
          <div className="ms-auto">
            <Select value={filter || 'all'} onValueChange={(v) => setFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-44 text-xs bg-white dark:bg-slate-800" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{ar ? 'كل الحالات' : 'All statuses'}</SelectItem>
                {Object.keys(STATUS).map((k) => <SelectItem key={k} value={k}>{STATUS[k][lang] || STATUS[k].en}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!data.invoices.length ? (
          <div className="text-sm text-[#111827]/50 dark:text-slate-400 py-10 text-center" data-testid="invoices-empty">{ar ? 'لا توجد فواتير بعد — ارفع أول دفعة أعلاه.' : 'No invoices yet — upload your first batch above.'}</div>
        ) : (
          <div className="divide-y divide-[#e5e7eb] dark:divide-slate-700">
            {data.invoices.map((inv) => {
              const st = STATUS[inv.status] || STATUS.extracted;
              const party = inv.kind === 'purchase' ? inv.vendor : inv.customer;
              const match = inv.matches?.[0];
              return (
                <div key={inv.id} className="py-3 flex flex-wrap gap-3 items-start" data-testid={`invoice-row-${inv.id}`}>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest font-bold ${inv.kind === 'sale' ? 'bg-blue-100 text-blue-700' : 'bg-rose-100 text-rose-700'}`} data-testid={`invoice-kind-${inv.id}`}>{inv.kind === 'sale' ? (ar ? 'مبيعات' : 'Sale') : (ar ? 'مشتريات' : 'Purchase')}</span>
                      <span className="font-semibold text-sm text-[#0a0a0a] dark:text-white" data-testid={`invoice-party-${inv.id}`}>{party || inv.filename}</span>
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${st.cls}`} data-testid={`invoice-status-${inv.id}`}><st.Icon className="w-3 h-3" /> {st[lang] || st.en}</span>
                    </div>
                    <div className="text-[11px] text-[#111827]/55 dark:text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                      {inv.invoice_number && <span>#{inv.invoice_number}</span>}
                      {inv.invoice_date && <span>{inv.invoice_date}</span>}
                      {inv.vat_amount != null && <span>VAT {fmt(inv.vat_amount)}</span>}
                      {inv.vendor_trn && <span>TRN {inv.vendor_trn}</span>}
                      <span>{inv.filename}</span>
                      {inv.confidence != null && inv.status !== 'posted' && <span>{ar ? 'الثقة' : 'conf.'} {Math.round(inv.confidence * 100)}%</span>}
                    </div>
                    {inv.error && <div className="text-[11px] text-rose-600 mt-1">{inv.error}</div>}
                    {inv.status !== 'posted' && match && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] rounded-md bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 px-2.5 py-1.5" data-testid={`invoice-match-${inv.id}`}>
                        <Link2 className="w-3.5 h-3.5 text-indigo-600" />
                        <span className="text-indigo-900 dark:text-indigo-100">{ar ? 'عملية بنكية مطابقة:' : 'Matching bank transaction:'} <b>{match.merchant || match.description}</b> · {match.date} · {fmt(match.amount)}{match.bank_name ? ` · ${match.bank_name}` : ''}</span>
                        <Button onClick={() => act(inv.id, () => saasPostInvoice(inv.id, 'match', match.id), ar ? 'تم ربط الفاتورة بالعملية البنكية' : 'Invoice linked to bank transaction')} disabled={acting === inv.id} className="h-7 px-2.5 text-[11px] rounded bg-indigo-600 hover:bg-indigo-700 text-white ms-auto" data-testid={`invoice-link-btn-${inv.id}`}>{ar ? 'ربط' : 'Link'}</Button>
                      </div>
                    )}
                  </div>
                  <div className="text-end">
                    <div className="font-display text-xl tabular-nums text-[#0a0a0a] dark:text-white" data-testid={`invoice-total-${inv.id}`}>{fmt(inv.total)} <span className="text-xs text-[#111827]/50">{inv.currency || 'AED'}</span></div>
                    <div className="mt-1 flex items-center justify-end gap-1">
                      <a href={saasInvoiceFileUrl(inv.id)} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-[#f1f5f9] dark:hover:bg-slate-800 text-[#111827]/60" title={ar ? 'عرض الملف' : 'View file'} data-testid={`invoice-view-${inv.id}`}><Eye className="w-3.5 h-3.5" /></a>
                      {inv.status !== 'posted' && <>
                        <button onClick={() => startEdit(inv)} className="p-1.5 rounded hover:bg-[#f1f5f9] dark:hover:bg-slate-800 text-[#111827]/60" title={ar ? 'تعديل' : 'Edit'} data-testid={`invoice-edit-${inv.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => act(inv.id, () => saasRematchInvoice(inv.id))} className="p-1.5 rounded hover:bg-[#f1f5f9] dark:hover:bg-slate-800 text-[#111827]/60" title={ar ? 'إعادة البحث عن مطابقة' : 'Re-check matches'} data-testid={`invoice-rematch-${inv.id}`}><RefreshCw className="w-3.5 h-3.5" /></button>
                        <Button onClick={() => act(inv.id, () => saasPostInvoice(inv.id, 'new'), ar ? 'تم ترحيل الفاتورة كعملية جديدة' : 'Invoice posted as a new transaction')} disabled={acting === inv.id || inv.status === 'failed'} className="h-7 px-2.5 text-[11px] rounded bg-[#0a0a0a] hover:bg-[#111827] text-white" data-testid={`invoice-post-btn-${inv.id}`}><PlusCircle className="w-3 h-3 me-1" /> {ar ? 'ترحيل' : 'Post'}</Button>
                      </>}
                      {inv.status === 'posted' && (
                        <button onClick={() => act(inv.id, () => saasUnpostInvoice(inv.id), ar ? 'تم إلغاء الترحيل' : 'Unposted')} className="p-1.5 rounded hover:bg-[#f1f5f9] dark:hover:bg-slate-800 text-[#111827]/60" title={ar ? 'إلغاء الترحيل' : 'Unpost'} data-testid={`invoice-unpost-${inv.id}`}><Undo2 className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => { if (window.confirm(ar ? 'حذف الفاتورة؟' : 'Delete this invoice?')) act(inv.id, () => saasDeleteInvoice(inv.id), ar ? 'تم الحذف' : 'Deleted'); }} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title={ar ? 'حذف' : 'Delete'} data-testid={`invoice-delete-${inv.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="invoice-edit-dialog">
          <DialogHeader><DialogTitle>{ar ? 'تعديل بيانات الفاتورة' : 'Edit invoice details'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#111827]/50 font-bold">{ar ? 'النوع' : 'Type'}</label>
              <Select value={form.kind || 'purchase'} onValueChange={(v) => setForm({ ...form, kind: v, category: v === 'sale' ? 'sales' : (form.category === 'sales' ? 'expense' : form.category) })}>
                <SelectTrigger className="h-9 mt-1 bg-white dark:bg-slate-800" data-testid="inv-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="purchase">{ar ? 'مشتريات (من مورّد)' : 'Purchase (from supplier)'}</SelectItem><SelectItem value="sale">{ar ? 'مبيعات (لعميل)' : 'Sale (to customer)'}</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#111827]/50 font-bold">{ar ? 'التصنيف' : 'Category'}</label>
              <Select value={form.category || 'expense'} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-9 mt-1 bg-white dark:bg-slate-800" data-testid="inv-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field k="vendor" label={ar ? 'المورّد / المُصدر' : 'Vendor / issuer'} form={form} setForm={setForm} />
            <Field k="customer" label={ar ? 'العميل' : 'Customer'} form={form} setForm={setForm} />
            <Field k="invoice_number" label={ar ? 'رقم الفاتورة' : 'Invoice number'} form={form} setForm={setForm} />
            <Field k="invoice_date" label={ar ? 'التاريخ' : 'Date'} form={form} setForm={setForm} type="date" />
            <Field k="subtotal" label={ar ? 'قبل الضريبة' : 'Subtotal'} form={form} setForm={setForm} type="number" />
            <Field k="vat_amount" label={ar ? 'الضريبة' : 'VAT'} form={form} setForm={setForm} type="number" />
            <Field k="total" label={ar ? 'الإجمالي' : 'Total'} form={form} setForm={setForm} type="number" />
            <Field k="vendor_trn" label={ar ? 'TRN المورّد' : 'Vendor TRN'} form={form} setForm={setForm} />
          </div>
          <DialogFooter>
            <Button onClick={() => setEditing(null)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">{ar ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={saveEdit} className="gold-button rounded-md h-10 px-5 text-xs" data-testid="inv-form-save">{ar ? 'حفظ' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
